<template>
  <div class="w-full flex justify-center pb-16 px-4 sm:px-8 mt-6">
    <div class="w-full max-w-5xl">

      <!-- EMPTY STATE -->
      <div v-if="allCompetency.total === 0" class="mt-8">
        <div class="vx-card p-10 modern-card empty-state-card text-center border-dashed border-2 border-gray-200 shadow-none bg-gray-50/50">
          <div class="mb-6">
            <h2 class="font-extrabold text-3xl text-gray-900 mb-2">{{ $t("home.h1") }}, {{ firstName }}!</h2>
            <p class="text-gray-500 text-lg">Your skill journey starts here.</p>
          </div>
          
          <div class="con-vs-avatar mx-auto my-10 flex justify-center items-center rounded-3xl shadow-sm bg-white" style="width: 240px; height: 180px;">
            <img src="@/assets/Illustrations/Ill_No Data.svg" alt="No Data" class="w-32 h-32 opacity-60 grayscale transition-all duration-500 hover:grayscale-0 hover:opacity-100 hover:scale-105" />
          </div>
          
          <div class="mb-8">
            <p class="text-gray-600 font-medium text-lg">{{ $t("home.h2") }}</p>
            <p class="text-gray-400 text-sm mt-2 max-w-md mx-auto">{{ $t("home.h3") }}</p>
          </div>
          
          <div class="flex justify-center">
            <vs-button class="px-10 py-3 shadow-lg hover-lift font-semibold text-lg" color="primary" type="filled" @click="addSkill">
              {{ $t("home.h4") }}
            </vs-button>
          </div>
        </div>
      </div>

      <!-- DASHBOARD STATE -->
      <div v-else>
        
        <!-- DASHBOARD HEADER -->
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 pb-6 border-b border-gray-100">
          <div class="mb-4 sm:mb-0">
            <h1 class="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-2">
              {{ $t("home.h1") }}, {{ firstName }}
            </h1>
            <p class="text-gray-500 font-medium text-base">{{ $t("home.h5") }}</p>
          </div>
          <vs-button color="primary" type="filled" icon-pack="feather" icon="icon-plus" class="shadow-md hover-lift font-semibold" @click="addSkill">
            Add New Skill
          </vs-button>
        </div>

        <!-- 1. CURRENT FOCUS (LATEST SKILL) -->
        <div v-if="latest" class="mb-12">
          <h3 class="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 ml-1">{{ $t("home.h6") || 'Current Focus' }}</h3>
          
          <div class="vx-card p-8 main-attraction-card bg-white relative overflow-hidden cursor-pointer" @click="latestSkillDetails(latest.i)">
            <div class="flex flex-col md:flex-row items-center justify-between relative z-10">
              
              <div class="flex flex-col items-center md:items-start text-center md:text-left md:w-5/12 mb-8 md:mb-0">
                <div style="position: relative; display: inline-block;" class="mb-6 icon-pulse-wrapper">
                  <span class="p-5 inline-flex rounded-full feather-icon select-none text-primary main-icon shadow-sm" style="background: rgba(var(--vs-primary),0.08); width: 90px; height: 90px; display: flex; align-items: center; justify-content: center;">
                    <img :src="latest.icon" alt="" width="56px" height="56px" class="greyscale transition-all duration-300" />
                  </span>
                  <span 
                    v-if="latest.motivationSurvey !== 1"
                    class="custom-status-dot main-status-dot warning shadow-md" 
                    :title="latest.motivationSurvey === 2 ? $t('playerJourney.t3_2') : $t('playerJourney.t3_3')"
                  ></span>
                </div>
                
                <div>
                  <h2 class="font-extrabold text-3xl text-gray-800 mb-1">{{ latest.comp }}</h2>
                  <p class="text-gray-500 text-sm font-medium">Keep up the momentum!</p>
                  <p v-if="latest.startDate" class="text-gray-400 text-xs mt-2 font-medium bg-gray-50 inline-block px-3 py-1 rounded-full border border-gray-100">
                    Started: {{ formatDate(latest.startDate) }}
                  </p>
                </div>
              </div>

              <div class="md:w-7/12 flex flex-col items-center justify-center w-full">
                <div v-if="latest.progress !== undefined" class="text-center w-full my-4 flex justify-center">
                  <ProgressMeasureChart 
                    :key="`chart-${latest.progress}-${latest.practice}-${latest.relevancy}`"
                    :progress="latest.progress" 
                    :practice="latest.practice" 
                    :relevancy="latest.relevancy" 
                  />
                </div>
                <vs-button class="w-full sm:w-2/3 md:w-3/4 shadow-lg hover-lift font-semibold mt-4" color="primary" type="filled" size="large" @click.stop="latestSkillDetails(latest.i)">
                  {{ $t("home.h7") }}
                </vs-button>
              </div>

            </div>
          </div>
        </div>
        
        <!-- 2. UP NEXT (OTHER IN-PROGRESS SKILLS) -->
        <div v-if="otherInProgress.length > 0" class="mb-12">
          <h3 class="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 ml-1">Recently Created / In Progress</h3>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div v-for="comp in otherInProgress" :key="comp.i" class="skill-card cursor-pointer bg-white rounded-xl p-5 border border-gray-100 flex items-center shadow-sm" @click="toSkillDetails(comp.i)">
              
              <div style="position: relative; display: inline-block;" class="flex-shrink-0">
                <span class="inline-flex rounded-full feather-icon select-none text-primary icon-bg" style="width: 56px; height: 56px; display: flex; align-items: center; justify-content: center;">
                  <img :src="comp.icon" alt="" width="32px" height="32px" class="card-img transition-transform duration-300" />
                </span>
                <span v-if="comp.motivationSurvey !== 1" class="custom-status-dot small warning"></span>
              </div>

              <div class="ml-4 flex-grow truncate">
                <h4 class="font-bold text-gray-800 text-lg mb-1 truncate">{{ comp.comp }}</h4>
                <div class="flex items-center space-x-2 text-xs">
                  <span class="text-primary font-semibold uppercase tracking-wider">In Progress</span>
                  <span v-if="comp.startDate" class="text-gray-400 font-medium hidden sm:inline-block">• Started {{ formatDate(comp.startDate) }}</span>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- 3. PENDING REVIEW (STRICT FILTER) -->
        <div v-if="pendingReviewSkills.length > 0" class="mb-10">
          <h3 class="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 ml-1">Pending Coach Approval</h3>
          
          <div class="bg-white rounded-2xl border shadow-sm overflow-hidden" style="border-color: rgba(var(--vs-warning), 0.3);">
            <div v-for="(comp, index) in pendingReviewSkills" :key="comp.i" class="completed-row cursor-pointer flex items-center p-4 transition-colors duration-200" :class="{ 'border-b border-gray-50': index !== pendingReviewSkills.length - 1 }" @click="toSkillDetails(comp.i)">
              
              <div class="flex-shrink-0 mr-4">
                <span class="inline-flex rounded-full" style="background-color: rgba(var(--vs-warning), 0.1); border: 1px solid rgba(var(--vs-warning), 0.2); width: 48px; height: 48px; display: flex; align-items: center; justify-content: center;">
                  <img :src="comp.icon" alt="" width="26px" height="26px" class="opacity-90" />
                </span>
              </div>

              <div class="flex-grow truncate">
                <h5 class="font-semibold text-gray-700 text-base m-0 mb-1">{{ comp.comp }}</h5>
                <p class="text-xs font-medium" style="color: rgba(var(--vs-warning), 1);">Waiting for your coach to review your proof of work.</p>
              </div>
              
              <div class="flex items-center space-x-2 px-3" style="color: rgba(var(--vs-warning), 1);">
                <span class="text-sm font-bold hidden sm:block uppercase tracking-wider">Under Review</span>
              </div>

            </div>
          </div>
        </div>

        <!-- 4. COMPLETED ACHIEVEMENTS (STRICT FILTER) -->
        <div v-if="completedSkills.length > 0" class="mb-10">
          <h3 class="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 ml-1">{{ $t("home.h8") || 'Completed Achievements' }}</h3>
          
          <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div v-for="(comp, index) in completedSkills" :key="comp.i" class="completed-row cursor-pointer flex items-center p-4 transition-colors duration-200" :class="{ 'border-b border-gray-50': index !== completedSkills.length - 1 }" @click="toSkillDetails(comp.i)">
              
              <div class="flex-shrink-0 mr-4">
                <span class="inline-flex rounded-full bg-gray-50 border border-gray-100 grayscale-icon" style="width: 48px; height: 48px; display: flex; align-items: center; justify-content: center;">
                  <img :src="comp.icon" alt="" width="26px" height="26px" class="opacity-70" />
                </span>
              </div>

              <div class="flex-grow truncate">
                <h5 class="font-semibold text-gray-700 text-base m-0 mb-1">{{ comp.comp }}</h5>
                <p v-if="comp.completedDate" class="text-xs text-gray-400 font-medium">Completed on {{ formatDate(comp.completedDate) }}</p>
              </div>
              
              <div class="flex items-center space-x-2 text-success px-3">
                <span class="text-sm font-semibold hidden sm:block">Completed</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-success"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
  import ProgressMeasureChart from "../components/ProgressMeasureChart";
  import constants from '../../../constants.js'
  import axios from 'axios';

  export default {
    name: "Home.vue",
    data() {
      return {
        allCompetency: { total: -1 }, latest: null, popupActivo4:false, popupActivo5:false,
        skillsCount:null, SkillsProgressCount:null, coachingSurveyDate:"",
        coachingSurveyStatus:'', coachingOverdueCounter:'', previous: [],
        CCD: this.$t("addNewSkill.data"), constants, coachingExperiencePopup: true,
        goingGreat: false, notSoGood: false, relationship: 0, resourceAvailability: 0,
        managerSupport: 0, traineeCoachId: null, coachingExperienceSaved: false,
        source: null, coachingSurveyDays: null
      };
    },
    props: { closeOnClickOutside: { default: true, type: Boolean } },
    components: { ProgressMeasureChart },
    computed: {
      firstName() {
        let userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        if (userInfo && userInfo.firstName) return userInfo.firstName;
        if (this.$store.state.AppActiveUser && this.$store.state.AppActiveUser.firstName) return this.$store.state.AppActiveUser.firstName;
        if (window.$cookies.get('firstName')) return window.$cookies.get('firstName');
        return "User"; 
      },
      disabledSubmitBtn() { return (this.relationship === 0 || this.resourceAvailability === 0 || this.managerSupport === 0); },
      
      // FIX 1: STRICT STRING MATCHING
      otherInProgress() { return this.previous.filter(s => s.progressStatus === 'in_progress'); },
      pendingReviewSkills() { return this.previous.filter(s => s.progressStatus === 'pending_review' || s.progressStatus === 'under_review'); },
      completedSkills() { return this.previous.filter(s => s.progressStatus === 'complete'); }
    },
    methods: {
      formatDate(dateString) {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
      },
      _searchInDataset(dataset, targetId) {
          if (!dataset || !targetId) return null;
          let parts = targetId.split("-");
          for (let key in dataset) {
              if (dataset[key] && dataset[key].competencies) {
                  let comps = dataset[key].competencies;
                  if (Array.isArray(comps)) {
                      let found = comps.find(c => c.id === targetId);
                      if (found) return found;
                  } else {
                      for (let k in comps) {
                          if (comps[k] && comps[k].id === targetId) return comps[k];
                          if (key === parts[0] && k === parts[1]) return comps[k];
                      }
                  }
              }
          }
          return null;
      },
      getSkillData(targetId) {
          if (!targetId) return null;
          let parts = targetId.split("-");
          if (parts.length >= 3) targetId = `${parts[0]}-${parts[1]}`;
          let locale = window.$cookies.get('locale') || (this.$i18n && this.$i18n.locale) || 'en';
          if (locale.includes('-')) locale = locale.split('-')[0];

          let patterns = [`i18n-${locale}-deep-Data`, `i18n-${locale}-pci-Data-Coach`, `i18n-${locale}-pci-Data`, `i18n-${locale}-peopletree-Data`, `i18n-${locale}-Data-DPM`, `i18n-${locale}-Data`];
          for (let i = 0; i < patterns.length; i++) {
              try {
                  const fallbackModule = require(`@/i18n/${patterns[i]}.js`);
                  const fallbackData = fallbackModule.default || fallbackModule;
                  if (fallbackData && fallbackData.addNewSkill && fallbackData.addNewSkill.data) {
                      let found = this._searchInDataset(fallbackData.addNewSkill.data, targetId);
                      if (found) return found;
                  }
              } catch (err) {}
          }
          return this._searchInDataset(this.CCD, targetId);
      },
      toSkillDetails(traineeCompetenciesId) { this.$router.push({ name: "skill-details", params: {traineeCompetenciesId} }); },
      addSkill() { this.$router.push({ name: "add-skill" }); },
      latestSkillDetails(id) { this.$router.push({ name: "skill-details", params: { traineeCompetenciesId: id } }); }
    },
    created() {
      if(window.$cookies.get('companyId')) { this.$router.push({name: "admin-user-list"}) } 
      else {
        if(this.$store.state.AppActiveUser.userType === "coach") this.$router.push({name: "user-profile"})
        else if(this.$store.state.AppActiveUser.userType === "admin") this.$router.push({name: "admin-user-list"})
      }

      const usersId = this.$store.state.AppActiveUser.usersId;
      
      // FIX 2: FETCH FROM BASE TABLE TO PREVENT BACKEND OVERRIDES
      this.$store.dispatch("trainee-competencies/find", { query: { usersId, $sort: { updatedAt: -1 } } })
        .then(async res => { 
          this.allCompetency = res;
          
          for (let i = 0; i < res.data.length; i++) {
            const compRecord = res.data[i];
            const compIdString = compRecord.competencyId;
            let compDetails = this.getSkillData(compIdString);
            
            let startDate = compRecord.createdAt || compRecord.created_at || null;
            let completedDate = compRecord.updatedAt || compRecord.updated_at || null;

            if (!compDetails) compDetails = { id: compIdString, comp: compRecord.skill_name || "Custom Skill", icon: "", wdd: "" };

            let finalSkillTitle = compDetails.comp;
            let pmTitles = [];

            try {
              let pmRes = await this.$store.dispatch("trainee-competencies-prgms/find", { 
                query: { trainee_competencies_id: compRecord.id } 
              });
              
              if (pmRes && pmRes.data && compDetails.progressMeasures) {
                pmRes.data.forEach(dbPm => {
                  let measureId = dbPm.progress_measure_id || dbPm.progressMeasureId || dbPm.prgms_id || dbPm.id;
                  let matchedPm = compDetails.progressMeasures.find(p => p.id === measureId);
                  if (matchedPm && matchedPm.title) { pmTitles.push(matchedPm.title); }
                });
              }
            } catch(err) { }

            if (pmTitles.length > 0) {
              finalSkillTitle = `${finalSkillTitle} (${pmTitles.join(', ')})`;
            }

            this.motivationSurvey = 0;
            if (compRecord.motivationSurveyDate !=null && compRecord.progressStatus === 'in_progress'){
              if(new Date() < new Date(compRecord.motivationSurveyDate)){
                this.motivationSurveyDate = compRecord.motivationSurveyDate; this.motivationSurvey=1;
              } else {
                this.motivationSurveyDate = compRecord.motivationSurveyDate;
                this.coachingOverdueCounter = Math.ceil(Math.abs(new Date() - new Date(this.motivationSurveyDate)) / (1000 * 60 * 60 * 24));
                this.motivationSurvey=2;
              }
            }

            if (!this.latest && compRecord.progressStatus === 'in_progress') {
              const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};
              const email = window.$cookies.get('email') || userInfo.email || "test@test.com";
              const orgId = this.$store.state.activeCoachOrgId || "default_org";
              const baseUrl =  "https://parser.ai.getboarded.com";
              
              let initialLevelVal = compRecord.initialLevel !== undefined && compRecord.initialLevel !== null ? parseInt(compRecord.initialLevel) : 5;
              let baseRelevancy = compRecord.relevancy !== undefined && compRecord.relevancy !== null ? compRecord.relevancy : (initialLevelVal * 10);

              this.latest = { 
                i: compRecord.id, id: compDetails.id, 
                comp: finalSkillTitle, 
                icon: compDetails.icon, wdd: compDetails.wdd, 
                motivationSurvey: this.motivationSurvey, progress: 0, practice: 0, relevancy: baseRelevancy,
                startDate: startDate
              };

              axios.get(`${baseUrl}/api/coach/progress?email=${encodeURIComponent(email)}&org_id=${encodeURIComponent(orgId)}`)
                .then(apiRes => {
                  if (apiRes.data && apiRes.data.data) {
                    this.latest.progress = apiRes.data.data.progress || 0;
                    this.latest.practice = apiRes.data.data.practice || 0;
                    this.latest.relevancy = baseRelevancy;
                  }
                }).catch(err => { });

            } else {
              this.previous.push({ 
                i: compRecord.id, id: compDetails.id, 
                comp: finalSkillTitle,
                icon: compDetails.icon, wdd: compDetails.wdd, motivationSurvey: this.motivationSurvey,
                progressStatus: compRecord.progressStatus,
                startDate: startDate,
                completedDate: completedDate
              });
            }
          }
        }).catch(e => {});
    }
  };
</script>

<style scoped>
/* Core Utility Styles */
.greyscale { mix-blend-mode: multiply; opacity: 0.9; }

.main-attraction-card {
  border-radius: 1.5rem !important;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(0,0,0,0.03);
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.4s ease;
}
.main-attraction-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 50px -15px rgba(0, 0, 0, 0.12) !important;
}

.icon-pulse-wrapper .main-icon {
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), background 0.3s ease;
}
.main-attraction-card:hover .icon-pulse-wrapper .main-icon {
  transform: scale(1.05);
  background: rgba(var(--vs-primary), 0.15) !important;
}
.main-attraction-card:hover .main-icon img {
  transform: scale(1.1);
}

.skill-card { transition: all 0.3s ease; }
.skill-card .icon-bg { background: rgba(var(--vs-primary), 0.06); transition: background 0.3s ease; }
.skill-card:hover { box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.06); transform: translateY(-3px); border-color: rgba(var(--vs-primary), 0.2); }
.skill-card:hover .icon-bg { background: rgba(var(--vs-primary), 0.12); }
.skill-card:hover .card-img { transform: scale(1.15); }

.completed-row { background: #ffffff; }
.completed-row:hover { background: #fdfdfd; }
.grayscale-icon { transition: filter 0.3s ease, opacity 0.3s ease; }
.completed-row:hover .grayscale-icon { filter: grayscale(0%); opacity: 1; }

.hover-lift { transition: transform 0.2s ease, box-shadow 0.2s ease; }
.hover-lift:hover { transform: translateY(-2px); }

.custom-status-dot {
  position: absolute; border-radius: 50%; border: 2px solid #ffffff; box-shadow: 0 2px 5px rgba(0,0,0,0.2); z-index: 10;
}
.custom-status-dot.main-status-dot { bottom: 0px; right: 0px; width: 22px; height: 22px; border-width: 3px; }
.custom-status-dot.small { bottom: -2px; right: -2px; width: 14px; height: 14px; border-width: 2px; }
.custom-status-dot.warning { background-color: #FBBF15 !important; }

.text-success { color: #28C76F !important; }
.text-warning { color: #F59E0B !important; }
</style>